import React, { useState, memo, useCallback, useMemo } from "react";
import { Post, PostStatus } from "@/core/types/postTypes";
import { PostCard } from "../PostCard";
import {
  PostContentSection,
  PostOverlay,
  PublishedPostStats,
} from "@/components/PostCard";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import type { DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { PostStatusDropdown } from "../PostStatusDropdown";
import { PostPreparationBadge } from "../components/PostPreparationBadge";
import { CaptionDisplay } from "../components/CaptionDisplay";
import { CaptionGeneratorModal } from "../components/CaptionGeneratorModal";
import { ManualCaptionModal } from "../components/ManualCaptionModal";
import { PostNotesModal } from "../components/PostNotesModal";
import { PostClientNotes } from "../components/PostClientNotes";
import { SchedulePostModal } from "../SchedulePostModal";
import { PublishModal } from "../PublishModal";
import { ScheduleErrorDisplay } from "../ScheduleErrorDisplay";
import { usePostModals } from "../hooks/usePostModals";
import { useCaptions } from "../../../hooks/useCaptions";
import { useAppStore } from "@/core/store/useAppStore";
import { useCaptionGenerationStore } from "../hooks/useCaptionGenerationStore";
import { usePostCardAuthOptional } from "../contexts/PostCardAuthContext";
import { UserRole } from "@/services/urlService";
import { postCacheUtils } from "../hooks/usePostsQuery";
import { useDeletePost } from "../hooks/useDeletePost";
import { postsService } from "../services/postsService";
import { PostItem } from "@/core/Post/PostItem.model";
import { apiClient } from "@/services/apiClient";
import { copyToClipboard } from "@/utils/clipboard";
import { GlobeIcon, SpinnerIcon, EditIcon, TrashIcon, RefreshIcon, DownloadIcon, ShareIcon, BoltIcon, ChartBarIcon } from "@/components/icons";
import { BoostModal } from "@/domains/Ads/components/BoostModal";
import { CampaignDetailsModal } from "@/domains/Ads/components/CampaignDetailsModal";
import { PostPerformanceModal } from "@/domains/Ads/components/PostPerformanceModal";
import { SponsoredBadge } from "@/domains/Ads/components/SponsoredBadge";
import { StandaloneAdPublishModal } from "@/domains/Ads/components/StandaloneAdPublishModal";
import { useLiveCampaignsForPost, usePaidImpressionsForPost } from "@/domains/Ads/hooks/useLiveCampaigns";
import type { Campaign } from "@/domains/Ads/types";
import { cn } from "@/utils/cn";

interface EditorPostCardProps {
  post: Post;
  onStatusChange: (post: Post, newStatus: PostStatus, response: unknown) => void;
  onPostDeleted: () => void;
  /** Optional permalink override per platform. The published-posts
   *  view doesn't need this (no platform-link UI on the card itself),
   *  but the reporting view passes them so the platform badges
   *  inside the stats panel become "open on FB / IG / TikTok" links. */
  statsPermalinks?: Partial<Record<string, string | null | undefined>>;
  /** Extra classes on the card container (e.g. `border-0` inside panels). */
  className?: string;
}

/**
 * Full admin editing card: wraps the core PostCard and adds status dropdown,
 * context menu (edit, schedule, re-prepare, download, delete), notes badge,
 * caption management, public sharing toggle, and preparation status.
 *
 * Used by: PostsView.tsx in grid mode.
 */
export const EditorPostCard: React.FC<EditorPostCardProps> = memo(({ post, onStatusChange, onPostDeleted, statsPermalinks, className }) => {
  const { openEditModal, openReelModal } = usePostModals();
  const { error: captionError } = useCaptions();
  const { selectedClient, captions, realtime } = useAppStore();
  const addToast = useAppStore((s) => s.addToast);
  const authContext = usePostCardAuthOptional();

  // State
  const [isHovered, setIsHovered] = useState(false);
  const [copiedPostId, setCopiedPostId] = useState(false);
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [showManualCaptionModal, setShowManualCaptionModal] = useState(false);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isTogglingPublic, setIsTogglingPublic] = useState(false);
  const [isRetryingPrep, setIsRetryingPrep] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isDownloadingFromMenu, setIsDownloadingFromMenu] = useState(false);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [boostMode, setBoostMode] = useState<"ai" | "manual">("ai");
  const [showStandaloneAdModal, setShowStandaloneAdModal] = useState(false);
  const [detailsCampaign, setDetailsCampaign] = useState<Campaign | null>(null);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  // Caption is hidden by default on published posts (no need to read
  // it once it's live). Toggle reveals the full block on demand.
  const [showCaptionExpanded, setShowCaptionExpanded] = useState(false);

  const liveCampaigns = useLiveCampaignsForPost(selectedClient?.id, post.id);
  const paidImpressions = usePaidImpressionsForPost(selectedClient?.id, post.id);
  const hasBothPlatformsLive =
    liveCampaigns.some((c) => c.platform === "FACEBOOK") &&
    liveCampaigns.some((c) => c.platform === "INSTAGRAM");

  const { deletePost, isDeleting } = useDeletePost({
    onSuccess: () => onPostDeleted(),
    // Without this, a backend rejection (e.g. post is in a grid) is
    // silent — the ConfirmDialog closes and the post stays in the list
    // with no explanation.
    onError: (message) => {
      addToast({ type: "error", message, duration: 6000 });
    },
  });

  // Publish progress from realtime
  const publishProgress = realtime.publishProgress[post.id];
  const isPublishing = publishProgress &&
    ["preparing", "prepared", "publishing"].includes(publishProgress.status);

  // Standalone-ad creation progress (fire-and-forget via SSE). Shows
  // an overlay badge on the post while the background task runs.
  const standaloneAdProgress = realtime.standaloneAdProgress[post.id];
  const isCreatingStandaloneAd = Boolean(
    standaloneAdProgress &&
      !["completed", "failed"].includes(standaloneAdProgress.phase),
  );
  const standaloneAdLabel = (() => {
    if (!standaloneAdProgress) return null;
    switch (standaloneAdProgress.phase) {
      case "queued":
        return "Ad queued…";
      case "preparing":
        return "Preparing media…";
      case "uploading_creative":
        return "Uploading creative…";
      case "creating_campaign":
        return "Creating campaign…";
      case "completed":
        return "Ad live";
      case "failed":
        return "Ad failed";
      default:
        return null;
    }
  })();

  const isPublishedStatus = post.status === PostStatus.PUBLISHED ||
    post.status === PostStatus.PARTIALLY_PUBLISHED;

  const activeGridPosition = post.activeGridPosition;
  const activeGridTotal = post.activeGridTotal;

  const generationState = useCaptionGenerationStore((state) => {
    const gen = state.generations[post.id];
    if (!gen) return null;
    const STALE_MS = 5 * 60 * 1000;
    if (gen.status === "generating" && (Date.now() - gen.startedAt) > STALE_MS) return null;
    return gen;
  });

  const captionData = captions[post.id] || {
    caption: null, isLoading: false, error: null, lastGenerated: null,
  };

  const isPublishableStatus =
    post.status === PostStatus.READY ||
    post.status === PostStatus.SCHEDULED ||
    post.status === PostStatus.PARTIALLY_PUBLISHED;

  const canDownload =
    post.status === PostStatus.READY ||
    post.status === PostStatus.SCHEDULED ||
    post.status === PostStatus.PARTIALLY_PUBLISHED ||
    post.status === PostStatus.PUBLISHED;

  const preparationStatus = post.postFolderPreparationStatus || "pending";
  const isPrepared = preparationStatus === "ready";

  // Handlers
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const handleEditPost = useCallback(() => {
    if (post.postType === "reel") openReelModal(post);
    else openEditModal(post);
  }, [openEditModal, openReelModal, post]);

  const handleGenerateCaption = useCallback(() => {
    if (!selectedClient) return;
    setShowCaptionModal(true);
  }, [selectedClient]);

  const handleRetryPreparation = useCallback(async () => {
    if (isRetryingPrep || !selectedClient) return;
    setIsRetryingPrep(true);
    try {
      const response = await postsService.retryPostPreparation(selectedClient.id, post.id);
      if (response.post) {
        const updatedPost = PostItem.fromLegacyPost(response.post, UserRole.ADMIN, selectedClient.id);
        postCacheUtils.upsertPost(selectedClient.id, updatedPost);
      }
    } catch (err) {
      console.error("Failed to retry preparation:", err);
    } finally {
      setIsRetryingPrep(false);
    }
  }, [isRetryingPrep, selectedClient, post.id]);

  const handleDownloadFromMenu = useCallback(async () => {
    if (isDownloadingFromMenu || !selectedClient) return;
    setIsDownloadingFromMenu(true);
    try {
      const response = await apiClient.post<{ downloadUrl: string }>(
        `/api/social-media/posts/${post.id}/download`,
        { clientId: selectedClient.id },
      );
      const link = document.createElement("a");
      link.href = response.downloadUrl;
      link.download = `${post.id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to download post:", err);
    } finally {
      setIsDownloadingFromMenu(false);
    }
  }, [isDownloadingFromMenu, selectedClient, post.id]);

  const handleDeleteConfirm = useCallback(async () => {
    setShowDeleteConfirm(false);
    await deletePost(post.id);
  }, [deletePost, post.id]);

  const handleMakePostPublic = useCallback(async () => {
    if (isTogglingPublic || !selectedClient) return;
    setIsTogglingPublic(true);
    try {
      await apiClient.post(`/client/${selectedClient.id}/posts/${post.id}/make-public`, undefined, { timeout: 120000 });
      postCacheUtils.invalidateAllPosts(selectedClient.id);
    } catch (err) {
      console.error("Failed to make post public:", err);
    } finally {
      setIsTogglingPublic(false);
    }
  }, [isTogglingPublic, selectedClient, post.id]);

  const handleRemovePostPublic = useCallback(async () => {
    if (isTogglingPublic || !selectedClient) return;
    setIsTogglingPublic(true);
    try {
      await apiClient.delete(`/client/${selectedClient.id}/posts/${post.id}/make-public`);
      postCacheUtils.invalidateAllPosts(selectedClient.id);
    } catch (err) {
      console.error("Failed to remove post public:", err);
    } finally {
      setIsTogglingPublic(false);
    }
  }, [isTogglingPublic, selectedClient, post.id]);

  // Toggle is_commercial flag. Used by the auto-boost flow's
  // `get_unboosted_published_posts` tool to filter candidates - only
  // posts flagged is_commercial become eligible for automated boosts.
  // Backend's generic /posts/:id PATCH accepts the field straight
  // through (PostEntity has the column, hasattr passes).
  const [isTogglingCommercial, setIsTogglingCommercial] = useState(false);
  const handleToggleCommercial = useCallback(async () => {
    if (isTogglingCommercial || !selectedClient) return;
    setIsTogglingCommercial(true);
    try {
      const next = !post.isCommercial;
      await postsService.updatePost(selectedClient.id, post.id, {
        postId: post.id,
        isCommercial: next,
      });
      postCacheUtils.invalidateAllPosts(selectedClient.id);
    } catch (err) {
      console.error("Failed to toggle is_commercial:", err);
    } finally {
      setIsTogglingCommercial(false);
    }
  }, [isTogglingCommercial, selectedClient, post.id, post.isCommercial]);

  const handleCaptionSaved = useCallback(() => {
    setShowCaptionModal(false);
    if (selectedClient?.id) postCacheUtils.refetchAndUpdatePost(selectedClient.id, post.id);
  }, [selectedClient?.id, post.id]);

  const handleManualCaptionSaved = useCallback(() => {
    setShowManualCaptionModal(false);
    if (selectedClient?.id) postCacheUtils.refetchAndUpdatePost(selectedClient.id, post.id);
  }, [selectedClient?.id, post.id]);

  // Context menu items
  const contextMenuItems = useMemo((): DropdownMenuEntry[] => {
    const items: DropdownMenuEntry[] = [
      {
        id: "edit",
        label: "Edit",
        icon: <EditIcon className="w-4 h-4" />,
        onClick: handleEditPost,
      },
    ];

    // Schedule / Reschedule
    if (post.status === PostStatus.READY || post.status === PostStatus.SCHEDULED) {
      items.push({
        id: "schedule",
        label: post.status === PostStatus.SCHEDULED ? "Reschedule" : "Schedule",
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
        onClick: () => setShowScheduleModal(true),
      });
    }

    // Re-prepare
    if (isPublishableStatus && preparationStatus === "ready") {
      items.push({
        id: "re-prepare",
        label: "Re-prepare",
        icon: <RefreshIcon className="w-4 h-4" />,
        onClick: handleRetryPreparation,
        loading: isRetryingPrep,
      });
    }

    // Download
    if (canDownload && isPrepared) {
      items.push({
        id: "download",
        label: "Download",
        icon: <DownloadIcon className="w-4 h-4" />,
        onClick: handleDownloadFromMenu,
        loading: isDownloadingFromMenu,
      });
    }

    // Publish
    if (canDownload && isPrepared) {
      items.push({
        id: "publish",
        label: "Publish",
        icon: <ShareIcon className="w-4 h-4" />,
        onClick: () => setShowPublishModal(true),
      });
    }

    // Performance — available on any published post regardless of
    // boost state; shows daily organic curves + paid overlay if any.
    if (isPublishedStatus && authContext?.userRole !== UserRole.CLIENT) {
      items.push({
        id: "performance",
        label: "Performance",
        icon: <ChartBarIcon className="w-4 h-4" />,
        onClick: () => setShowPerformanceModal(true),
      });
    }

    // Mark commercial / non-commercial. Gate on admin only; this is an
    // ads-pipeline signal the client portal shouldn't touch.
    if (authContext?.userRole !== UserRole.CLIENT) {
      items.push({
        id: "toggle-commercial",
        label: post.isCommercial ? "Mark non-commercial" : "Mark commercial",
        icon: <BoltIcon className="w-4 h-4" />,
        onClick: handleToggleCommercial,
        loading: isTogglingCommercial,
      });
    }

    // Boost — two entry points: AI-driven (default) and manual (bypass
    // the agent). Hidden only when both FB and IG already have live
    // campaigns; if one's free, boosting can still cover that platform.
    if (isPublishedStatus && authContext?.userRole !== UserRole.CLIENT && !hasBothPlatformsLive) {
      items.push({
        id: "boost",
        label: liveCampaigns.length > 0 ? "Boost other platform" : "Boost",
        icon: <BoltIcon className="w-4 h-4" />,
        onClick: () => {
          setBoostMode("ai");
          setShowBoostModal(true);
        },
      });
      items.push({
        id: "boost-manual",
        label: "Boost manually",
        icon: <BoltIcon className="w-4 h-4" />,
        onClick: () => {
          setBoostMode("manual");
          setShowBoostModal(true);
        },
      });
    }

    // Standalone ad — separate creative uploaded fresh to Meta. Doesn't
    // require the post to be published organically, so available as soon
    // as the post has media.
    if (authContext?.userRole !== UserRole.CLIENT && (post.mediaItems?.length ?? 0) > 0) {
      items.push({
        id: "standalone-ad",
        label: "Publish as standalone ad",
        icon: <BoltIcon className="w-4 h-4" />,
        onClick: () => setShowStandaloneAdModal(true),
      });
    }

    // Public sharing
    if (isPublishedStatus && authContext?.userRole !== UserRole.CLIENT) {
      if (post.publicStatus === "public") {
        items.push({
          id: "remove-public",
          label: "Remove Public Link",
          icon: <GlobeIcon className="w-4 h-4" />,
          onClick: handleRemovePostPublic,
          loading: isTogglingPublic,
        });
      } else if (post.publicStatus !== "copying" && !isTogglingPublic) {
        items.push({
          id: "make-public",
          label: "Make Public",
          icon: <GlobeIcon className="w-4 h-4" />,
          onClick: handleMakePostPublic,
          loading: isTogglingPublic,
        });
      }
    }

    // Separator + Delete
    items.push({ id: "separator", separator: true as const });
    items.push({
      id: "delete",
      label: "Delete",
      icon: <TrashIcon className="w-4 h-4" />,
      onClick: () => setShowDeleteConfirm(true),
      variant: "danger" as const,
    });

    return items;
  }, [
    post.status, post.publicStatus, isPublishableStatus, isPublishedStatus,
    preparationStatus, canDownload, isPrepared,
    isRetryingPrep, isDownloadingFromMenu, isTogglingPublic,
    handleEditPost, handleRetryPreparation, handleDownloadFromMenu,
    handleMakePostPublic, handleRemovePostPublic, authContext?.userRole,
    liveCampaigns, hasBothPlatformsLive,
    post.isCommercial, handleToggleCommercial, isTogglingCommercial,
  ]);

  // Overlay: top-left (notes badge + status dropdown on hover)
  const overlayTopLeft = (
    <PostOverlay.TopLeft className="flex flex-col gap-1.5">
      {/* One Sponsored badge per live campaign (up to two: FB + IG) */}
      {liveCampaigns.map((c) => (
        <SponsoredBadge
          key={c.id}
          platform={c.platform}
          status={c.status}
          creativeSource={c.creativeSource}
          onClick={() => setDetailsCampaign(c)}
        />
      ))}


      {/* Notes badge (always visible when count > 0) */}
      {(post.notesCount ?? 0) > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowNotesPanel(true);
          }}
          className="px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded-full shadow-xs hover:bg-amber-600 transition-colors"
          data-interactive="true"
        >
          {post.notesCount} {post.notesCount === 1 ? "note" : "notes"}
        </button>
      )}

      {/* Status dropdown (on hover) */}
      {isHovered && (
        <PostStatusDropdown
          post={post}
          onStatusChange={onStatusChange}
        />
      )}
    </PostOverlay.TopLeft>
  );

  // Overlay: top-right (context menu on hover, hidden when publishing)
  const overlayTopRight = isHovered ? (
    <PostOverlay.TopRight>
      <DropdownMenu
        items={contextMenuItems}
        width="w-48"
        menuClassName="rounded-lg py-1"
        trigger={
          <button
            className={cn(
              "p-1.5 rounded-full shadow-xs transition-all hover:shadow-md",
              "bg-white/90 hover:bg-white text-gray-700"
            )}
            title="Actions"
            data-interactive="true"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
              />
            </svg>
          </button>
        }
      />
    </PostOverlay.TopRight>
  ) : undefined;

  return (
    <>
      <PostCard
        post={post}
        viewMode="grid"
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        overlayTopLeft={overlayTopLeft}
        overlayTopRight={overlayTopRight}
      >
        <PostContentSection viewMode="grid">
          {/* Header row: Copy ID, grid position, download pill, public toggle */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Copy ID */}
            <button
              onClick={() => {
                copyToClipboard(post.id);
                setCopiedPostId(true);
                setTimeout(() => setCopiedPostId(false), 2000);
              }}
              className={`p-1 rounded-md transition-colors ${
                copiedPostId
                  ? "bg-green-100 text-green-600"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
              }`}
              title={copiedPostId ? "Copied!" : `Copy ID: ${post.id}`}
            >
              {copiedPostId ? (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
              )}
            </button>

            {/* Grid position badge */}
            {activeGridPosition != null && activeGridTotal != null && (
              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                #{activeGridTotal - activeGridPosition}
              </span>
            )}

            {/* Commercial badge — visible when flagged for auto-boost. */}
            {post.isCommercial && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full"
                title="Flagged for auto-boost consideration"
              >
                <BoltIcon className="w-3 h-3" />
                Commercial
              </span>
            )}

            {/* Standalone-ad progress pill — stays put once a task runs.
                Transient in-progress / completed states auto-clear via
                useRealtimeSync; failed state persists indefinitely until
                a new task overwrites it with a queued → ... → completed
                sequence, at which point the completed state auto-clears
                and leaves a clean slate. Title attribute carries the
                full agent/error message on hover. */}
            {standaloneAdLabel && (
              <span
                className={cn(
                  "inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded-full",
                  isCreatingStandaloneAd
                    ? "bg-blue-100 text-blue-700"
                    : standaloneAdProgress?.phase === "completed"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700",
                )}
                title={standaloneAdProgress?.message || standaloneAdLabel}
              >
                {isCreatingStandaloneAd && <SpinnerIcon className="w-3 h-3" />}
                {!isCreatingStandaloneAd && <BoltIcon className="w-3 h-3" />}
                <span>{standaloneAdLabel}</span>
              </span>
            )}

          </div>

          {/* Post preparation badge */}
          <div className="mt-2">
            <PostPreparationBadge post={post} clientId={authContext?.clientId} />
          </div>

          {/* Group name */}
          {post.groupName && (
            <div className="mt-2 text-xs text-gray-500 font-medium truncate">
              {post.groupName}
            </div>
          )}

          {/* Caption section. Hidden by default on published posts —
              once the post is live the caption is read-only context,
              not actionable. Collapsed state keeps an "Show caption"
              affordance so it's still recoverable. */}
          <div className="mt-2">
            {post.caption && isPublishedStatus && !showCaptionExpanded ? (
              <button
                onClick={() => setShowCaptionExpanded(true)}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Show caption
              </button>
            ) : post.caption ? (
              <CaptionDisplay
                caption={post.caption}
                title="Caption"
                icon={
                  <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                }
                bgColor="bg-blue-50"
                textColor="text-blue-700"
                borderColor="border-blue-200"
                onEdit={() => setShowManualCaptionModal(true)}
                canEdit={authContext?.userRole !== UserRole.CLIENT}
                onRegenerate={handleGenerateCaption}
                canRegenerate={authContext?.userRole !== UserRole.CLIENT}
                isRegenerating={captionData.isLoading || generationState?.status === "generating"}
              />
            ) : generationState?.status === "ready" ? (
              /* AI caption suggestion available */
              <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  <span className="text-xs font-medium text-green-700">AI Caption Ready</span>
                </div>
                <button
                  onClick={handleGenerateCaption}
                  className="text-xs text-green-600 hover:text-green-800 font-medium underline"
                >
                  View and apply
                </button>
              </div>
            ) : null}

            {/* Caption error display */}
            {captionError && (
              <div className="mt-1 text-xs text-red-500">{captionError}</div>
            )}

            {/* Client notes (inline, below caption) */}
            {selectedClient && (
              <PostClientNotes
                clientId={selectedClient.id}
                postId={post.id}
              />
            )}

            {/* No caption: generate + write manually buttons.
                Suppressed on published posts (the affordances aren't
                useful once the post is live). */}
            {!post.caption && !isPublishedStatus && generationState?.status !== "ready" && authContext?.userRole !== UserRole.CLIENT && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleGenerateCaption}
                  disabled={captionData.isLoading || generationState?.status === "generating" || !selectedClient}
                  className="relative text-xs text-purple-600 hover:text-purple-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {captionData.isLoading || generationState?.status === "generating" ? (
                    <SpinnerIcon className="w-3 h-3" />
                  ) : (
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  )}
                  Generate AI Caption
                </button>
                <button
                  onClick={() => setShowManualCaptionModal(true)}
                  className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                >
                  Write Manually
                </button>
              </div>
            )}
          </div>

          {/* Per-platform stats — always rendered on published cards
              for consistent grid heights. The component handles its
              own "no data yet" state when metrics_by_platform is null
              (post just published, not synced yet). */}
          {isPublishedStatus && (
            <PublishedPostStats
              metricsByPlatform={post.metricsByPlatform}
              permalinks={statsPermalinks}
              paidImpressions={paidImpressions}
            />
          )}

          {/* Bottom section */}
          <div className="mt-2 space-y-2">
            {/* Schedule error display */}
            <ScheduleErrorDisplay post={post} />

            {/* Client review status — admin signal that the client has
                acknowledged the post via the review page. Independent
                of the post status lifecycle (admin owns that). */}
            {post.clientReviewedAt && (
              <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center gap-2">
                  <svg className="w-3 h-3 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-xs font-medium text-emerald-700">
                    Approvato dal cliente · {new Date(post.clientReviewedAt).toLocaleDateString("it-IT")}
                  </span>
                </div>
              </div>
            )}

            {/* Hashtags */}
            {post.hashtags && (
              <div className="flex flex-wrap gap-1">
                {post.hashtags
                  .split(/[\s,]+/)
                  .filter(Boolean)
                  .map((tag, index) => (
                    <span
                      key={index}
                      className="text-blue-600 text-xs hover:text-blue-800 cursor-pointer"
                    >
                      {tag.startsWith("#") ? tag : `#${tag}`}
                    </span>
                  ))}
              </div>
            )}

            {/* Published date */}
            {post.publishedAt && (
              <div className="text-xs text-gray-400">
                Published {new Date(post.publishedAt).toLocaleDateString()}
              </div>
            )}
          </div>
        </PostContentSection>
      </PostCard>

      {/* Modals */}
      <CaptionGeneratorModal
        isOpen={showCaptionModal}
        onClose={() => setShowCaptionModal(false)}
        post={post}
        clientId={selectedClient?.id || ""}
        onCaptionSaved={handleCaptionSaved}
      />

      <ManualCaptionModal
        isOpen={showManualCaptionModal}
        onClose={() => setShowManualCaptionModal(false)}
        post={post}
        clientId={selectedClient?.id || ""}
        initialCaption={post.caption || ""}
        onCaptionSaved={handleManualCaptionSaved}
      />

      <PostNotesModal
        postId={post.id}
        notesCount={post.notesCount ?? 0}
        isOpen={showNotesPanel}
        onClose={() => setShowNotesPanel(false)}
      />

      {showScheduleModal && selectedClient && (
        <SchedulePostModal
          post={post}
          clientId={selectedClient.id}
          onClose={() => setShowScheduleModal(false)}
          onScheduled={() => {
            setShowScheduleModal(false);
            postCacheUtils.refetchAndUpdatePost(selectedClient.id, post.id);
          }}
        />
      )}

      {showPublishModal && selectedClient && (
        <PublishModal
          post={post}
          clientId={selectedClient.id}
          onClose={() => setShowPublishModal(false)}
        />
      )}

      <BoostModal
        postId={showBoostModal ? post.id : null}
        isOpen={showBoostModal}
        onClose={() => setShowBoostModal(false)}
        initialMode={boostMode}
      />

      <StandaloneAdPublishModal
        post={showStandaloneAdModal ? post : null}
        isOpen={showStandaloneAdModal}
        onClose={() => setShowStandaloneAdModal(false)}
      />

      <CampaignDetailsModal
        campaign={detailsCampaign}
        isOpen={!!detailsCampaign}
        onClose={() => setDetailsCampaign(null)}
      />

      <PostPerformanceModal
        postId={showPerformanceModal ? post.id : null}
        isOpen={showPerformanceModal}
        onClose={() => setShowPerformanceModal(false)}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
      />
    </>
  );
});

EditorPostCard.displayName = "EditorPostCard";
